import exchanges from '@/constants/exchanges';
import ReportSubSection from '@components/ReportSubSection';
import ReportNextSteps from '@components/ReportNextSteps';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_MANAGEMENT_ANCHOR,
  COMPANY_INFORMATION_ANCHOR,
} from '@/constants/anchors';
import { kebabCase } from 'lodash';
import useInvestingIdea from '@/pages/CompanyReport/hooks/useInvestingIdea';
import { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { useVisibleSectionsIsPristine } from '../../../VisibleSectionsContextProvider';

const NextSteps = loadable(
  () => import('@/pages/CompanyReport/components/Overview/components/NextSteps')
);

export interface Props {
  scorePast: number;
  countryISO: string;
  scoreValue: number;
  scoreFuture: number;
  companyName: string;
  tickerSymbol: string;
  scoreDividend: number;
  exchangeSymbol: string;
  CEOCompensation: null | number;
  isDataMeaningless: boolean;
  industryPrimaryName: string;
}

const HealthNextSteps = ({
  scorePast,
  countryISO,
  scoreValue,
  scoreFuture,
  companyName,
  tickerSymbol,
  scoreDividend,
  exchangeSymbol,
  CEOCompensation,
  isDataMeaningless,
  industryPrimaryName,
}: Props) => {
  const isPristine = useVisibleSectionsIsPristine();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'HealthNextSteps',
  });
  const payload = useInvestingIdea(
    'healthy-companies',
    GridViewOptions.GRID_VIEW_HEALTHY,
    countryISO.toLowerCase(),
    kebabCase(industryPrimaryName)
  );
  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title="Discover healthy companies"
        body={
          <ReportNextSteps>
            {isDataMeaningless && (
              <>
                {scoreValue > 2 ? (
                  <li>
                    <span>{`Examine whether ${companyName} is trading at `}</span>
                    <a
                      href={`#${COMPANY_VALUE_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_VALUE_ANCHOR,
                        })
                      }
                    >
                      an attractive price
                    </a>
                    <span>{` based on how much it is expected to earn in the future, and relative to its industry peers and the wider market.`}</span>
                  </li>
                ) : scorePast > 3 ? (
                  <li>
                    <span>Check out </span>
                    <a
                      href={`#${COMPANY_PAST_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_PAST_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s track record over time`}
                    </a>
                    {` to understand how much the company has returned to shareholders and its earnings growth rate in the past.`}
                  </li>
                ) : scoreFuture > 2 ? (
                  <li>
                    <span>Check out </span>
                    <a
                      href={`#${COMPANY_FUTURE_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_FUTURE_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s forecasted future earnings and revenues`}
                    </a>
                    <span>
                      {` from industry analysts to understand how the company is expected to perform in the future against its competitors and the wider market.`}
                    </span>
                  </li>
                ) : scoreDividend > 3 ? (
                  <li>
                    <span>{`${companyName} is a dividend payer. Have a look at `}</span>
                    <a
                      href={`#${COMPANY_DIVIDEND_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_DIVIDEND_ANCHOR,
                        })
                      }
                    >
                      its current yield, dividend levels over time
                    </a>
                    <span>
                      {` and whether analysts believe the payout ratio will be sustainable moving forward.`}
                    </span>
                  </li>
                ) : CEOCompensation !== null ? (
                  <li>
                    <span>{`Take a look at our analysis of `}</span>
                    <a
                      href={`#${COMPANY_MANAGEMENT_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_MANAGEMENT_ANCHOR,
                        })
                      }
                    >
                      {`${tickerSymbol}’s management`}
                    </a>
                    <span>
                      {` and see if the CEO’s compensation is within a reasonable range, who is on the board and if insiders have been trading lately.`}
                    </span>
                  </li>
                ) : null}

                {exchangeSymbol === 'OTCPK' ? (
                  <li>
                    <span>
                      {`Investors tend to look at the financial health of a company this size in order to assess the sustainability of its current operations. While we do not consider unaudited financials to be a reliable enough to include in our analysis, you can access them on the `}
                    </span>
                    <a
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      href={`${
                        exchanges[exchangeSymbol]
                      }/stock/${tickerSymbol.replace('.', '')}/financials`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      OTC Markets Website
                    </a>
                    .
                  </li>
                ) : exchanges[exchangeSymbol] ? (
                  <li>
                    <span>{`Investors tend to look at the financial health of a company this size in order to assess the sustainability of its current operations. ${companyName}'s cash and debt levels may be found in its `}</span>
                    <a
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      href={`${exchanges[exchangeSymbol]}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      annual reports archived here
                    </a>
                    .
                  </li>
                ) : null}
                <li>
                  <span>
                    {`Financial health is measured at one point in time, so the latest financial report is the best representation of the company’s current financial status. Check when ${companyName}'s financial data was last updated `}
                  </span>
                  <a
                    href={`#${COMPANY_INFORMATION_ANCHOR}`}
                    onClick={() =>
                      trackEvent({
                        action: 'click',
                        modifier: 'other-section',
                        destinationSection: COMPANY_INFORMATION_ANCHOR,
                      })
                    }
                  >
                    here
                  </a>
                  .
                </li>
              </>
            )}
            <li>
              {typeof payload !== 'undefined' && (
                <NextSteps
                  link={payload.linkToInvestingIdea}
                  industry={industryPrimaryName}
                  section="health"
                />
              )}
            </li>
          </ReportNextSteps>
        }
      />
    </Track>
  );
};

export default HealthNextSteps;
